import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL, categoryPageSlugs } from '@boss/constants/b2b-b2c';
import { contentfulContext } from '@boss/contentful-client';
import { category as categoryService, content as contentService } from '@boss/services';
import type { LOCALE_CODE } from '@boss/types/b2b-b2c';

import { SEO } from '../../components';
import { IS_BUILD, PageTypes } from '../../constants';
import { CategoryOverviewPage } from '../../containers';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket, mapNextToContentfulMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const getStaticProps = async ({
  preview,
  locale,
}: GetStaticPropsContext<{ slug: string[]; locale: LOCALE_CODE }>) => {
  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  // SET GLOBAL MARKET CONTEXT
  contentfulContext.market = market;

  const [navigation, overviewPage, allCategories] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    contentService.getOverviewPage({
      locale: contentfulLocaleMarket,
      channel: CHANNEL,
      type: 'Category overview',
      batchMode: IS_BUILD,
      preview: !!preview,
      market,
    }),
    categoryService.getAll(contentfulLocaleMarket, categoryService.DEPTH_LEAF, IS_BUILD),
  ]);

  const categories = allCategories.filter(category => category.breadcrumb.length < categoryService.DEPTH_LEVEL2);
  const popularCategories = allCategories.filter(
    category => category.settings?.includes('popular category') && category.breadcrumb.length >= 2,
  );

  return {
    props: {
      pageType: PageTypes.CATEGORY_OVERVIEW,
      preview: !!preview,
      pageData: { categories, popularCategories, overviewPage },
      navigation,
      ...(await getServerSideTranslations(locale, ['common', 'forms'])),
    },
  };
};

export const Category = ({ pageData, navigation, preview }: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();
  const pageTitle = t('overview');
  const pageDescription = pageTitle; // TODO: Update with final value once this is known

  const slugs = categoryPageSlugs;

  const { categories, popularCategories, overviewPage } = pageData;

  return (
    <SEO
      description={overviewPage?.metaDescription ?? pageDescription}
      imageSrc={overviewPage?.ogImage?.image?.file?.url}
      localizedSlugs={slugs}
      navigation={navigation}
      noFollow={overviewPage?.noFollow}
      noIndex={overviewPage?.noIndex}
      title={overviewPage?.pageTitle ?? pageTitle}
    >
      <Layout crumbles={[]} localizedSlugs={slugs} navigation={navigation} preview={preview}>
        <CategoryOverviewPage categories={categories} cmsData={overviewPage} popularCategories={popularCategories} />
      </Layout>
    </SEO>
  );
};

export default Category;
